import { collection, query, where, getDocs, onSnapshot } from 'firebase/firestore';
import { Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import { BaseCRUDManager } from '../base/BaseCRUDManager';
import { isDemoMode } from '../../utilities/demoMode';
import { logger } from '../../utilities/logger';
import { createDummySubmission, createDummyPinWithAlbum } from '../../services/demoDummyData';
import { DemoDataManager } from '../../services/demoDataManager';
import RecordingStore from '../../stores/RecordingStore';
import { v4 as uuidv4 } from 'uuid';
import { IRecording, IRecordingWithAuthor } from '../../types';
import { hasSubmissionsResponsesTutorialStarted } from './types';
import { getRecordingsByProjectKey } from './queries';
import { attachAuthorToEntity } from '../base/authorAttachment';
import { sortByDate } from '../../utilities/timestampUtils';

const RECORDINGS_COLLECTION = 'Recordings';

/**
 * Get all user submissions for a project
 */
export async function getSubmissionsByProject(projectId: string): Promise<IRecordingWithAuthor[]> {
  // Only log once per project to avoid spam
  const logKey = `getSubmissions_${projectId}`;
  if (!(window as any)[logKey]) {
    logger.info('getSubmissionsByProject called', { projectId, isDemoMode: isDemoMode() });
    (window as any)[logKey] = true;
    setTimeout(() => delete (window as any)[logKey], 5000);
  }
  
  if (isDemoMode()) {
    const recordings = DemoDataManager.getRecordings(projectId);
    logger.debug('Demo mode: fetched recordings', { 
      projectId, 
      totalRecordings: recordings.length,
      recordings: recordings.map(r => ({
        key: r.key,
        isUserRecording: r.isUserRecording,
        approvalStatus: r.approvalStatus,
        submittedAt: r.submittedAt,
        pinKey: r.pinKey,
        project: r.project
      }))
    });
    
    let submissions = recordings
      .filter(rec => {
        const isSubmission = rec.isUserRecording && rec.isSubmission === true;
        if (!isSubmission && rec.isUserRecording) {
          logger.debug('User recording filtered out - not marked as submission', {
            key: rec.key,
            isUserRecording: rec.isUserRecording,
            isSubmission: rec.isSubmission
          });
        }
        return isSubmission;
      }) as IRecordingWithAuthor[];
    
    // Add submissions to RecordingStore
    if (submissions.length > 0) {
      submissions.forEach(submission => {
        RecordingStore.addRecording(submission);
      });
    }
    
    logger.info('Demo mode: filtered submissions', { 
      projectId, 
      submissionCount: submissions.length,
      submissions: submissions.map(s => ({
        key: s.key,
        approvalStatus: s.approvalStatus,
        submittedAt: s.submittedAt
      }))
    });
    
    // Initialize with dummy submission if empty AND tutorial has been started
    if (submissions.length === 0) {
      const tutorialStarted = hasSubmissionsResponsesTutorialStarted();
      if (!tutorialStarted) {
        return [];
      }
      
      logger.info('Tutorial started, creating dummy submission', {
        projectId,
        tutorialFlow: localStorage.getItem('demo_tutorial_flow'),
        completedSteps: localStorage.getItem('demo_tutorial_progress')
      });
      
      // Check if a submission already exists
      const existingSubmissions = DemoDataManager.getRecordings(projectId).filter(r => 
        r.isSubmission === true && r.isDemo === true
      );
      
      if (existingSubmissions.length > 0) {
        logger.info('Dummy submission already exists, using existing', { 
          submissionCount: existingSubmissions.length 
        });
        return existingSubmissions;
      }
      
      // Get a pin to associate the submission with
      const pins = DemoDataManager.getPins(projectId);
      let firstPin = pins[0];
      
      const existingBirminghamPin = pins.find(p => p.name === 'Birmingham Demo Pin' && p.isDemo);
      
      if ((!firstPin || !firstPin.albumKey) && !existingBirminghamPin) {
        const creationKey = `demo_pin_creation_${projectId}`;
        const isCreating = localStorage.getItem(creationKey);
        
        if (isCreating !== 'true') {
          const result = await createDummyPinWithAlbum(projectId);
          
          if (result) {
            const pinsAfterCheck = DemoDataManager.getPins(projectId);
            const duplicateCheck = pinsAfterCheck.find(p => 
              (p.name === 'Birmingham Demo Pin' && p.isDemo) || p.pinKey === result.pin.pinKey
            );
            
            if (!duplicateCheck) {
              await DemoDataManager.createAlbum(result.album);
              DemoDataManager.createPin(result.pin);
              firstPin = result.pin;
              logger.info('Created dummy pin with Birmingham location', { 
                pinKey: result.pin.pinKey, 
                albumKey: result.album.albumKey 
              });
            } else {
              logger.info('Dummy pin already exists (race condition detected), using existing pin', { 
                pinKey: duplicateCheck.pinKey 
              });
              firstPin = duplicateCheck;
            }
          }
        } else {
          logger.info('Dummy pin creation already in progress, waiting...');
          let retries = 0;
          const maxRetries = 3;
          while (retries < maxRetries && !firstPin) {
            await new Promise(resolve => setTimeout(resolve, 500));
            const pinsAfterWait = DemoDataManager.getPins(projectId);
            const pinAfterWait = pinsAfterWait.find(p => p.name === 'Birmingham Demo Pin' && p.isDemo);
            if (pinAfterWait) {
              firstPin = pinAfterWait;
              logger.info('Found pin after waiting', { pinKey: firstPin.pinKey });
              break;
            }
            retries++;
          }
          
          if (!firstPin) {
            const submissionsAfterWait = DemoDataManager.getRecordings(projectId).filter(r => 
              r.isSubmission === true && r.isDemo === true
            );
            if (submissionsAfterWait.length > 0) {
              logger.info('Submission was created by another call while waiting for pin', { 
                submissionCount: submissionsAfterWait.length 
              });
              return submissionsAfterWait;
            }
          }
        }
      } else if (existingBirminghamPin) {
        firstPin = existingBirminghamPin;
        logger.info('Using existing Birmingham demo pin', { pinKey: existingBirminghamPin.pinKey });
      }
      
      // Only create submission if we have a valid pin
      if (firstPin && firstPin.albumKey) {
        const submissionsBeforeCreate = DemoDataManager.getRecordings(projectId).filter(r => 
          r.isSubmission === true && r.isDemo === true
        );
        
        if (submissionsBeforeCreate.length > 0) {
          logger.info('Submission already exists, skipping creation', { 
            submissionCount: submissionsBeforeCreate.length 
          });
          return submissionsBeforeCreate;
        }
        
        const dummySubmission = createDummySubmission(firstPin, projectId);
        
        if (dummySubmission) {
          DemoDataManager.createRecording(dummySubmission);
              RecordingStore.addRecording(dummySubmission);
          submissions = [dummySubmission];
          logger.info('Created dummy submission with pin', { 
            submissionKey: dummySubmission.key, 
            pinKey: firstPin.pinKey 
          });
        }
      } else {
        const finalSubmissionsCheck = DemoDataManager.getRecordings(projectId).filter(r => 
          r.isSubmission === true && r.isDemo === true
        );
        
        if (finalSubmissionsCheck.length === 0) {
          logger.warn('No pin available, creating placeholder submission (should not happen in normal flow)');
          const threeDaysAgo = Timestamp.fromMillis(Timestamp.now().toMillis() - 3 * 24 * 60 * 60 * 1000);
          
          const dummySubmission: IRecordingWithAuthor = {
            key: uuidv4(),
            albumKey: 'demo-album-placeholder',
            pinKey: 'demo-pin-placeholder',
            project: projectId,
            isUserRecording: true,
            isDemo: true,
            createdAt: threeDaysAgo,
            isSubmission: true,
            submittedAt: threeDaysAgo,
            approvalStatus: 'pending',
            file: {
              recordingPath: 'recordings/demo/The Brook by Alfred Lord Tennyson_1.wav',
              title: 'The Brook by Alfred Lord Tennyson',
              description: 'This is a user-submitted recording about their experience. It needs to be reviewed and rated before being published.',
              size: 2048000,
              audioLength: '02:30'
            },
            ownership: {
              recordingAuthor: 'demo-author-submission-1',
              recordingOwner: 'demo-user-submission-1',
              recordingNarrator: 'demo-narrator-1'
            },
            tags: {
              subjectTags: ['user-submission', 'story'],
              genreTags: ['narrative']
            },
            requiresModeration: true,
            contentRating: undefined,
            author: {
              authorKey: 'demo-author-submission-1',
              name: 'Sarah Johnson',
              bio: 'Local community member passionate about sharing stories and experiences. Enjoys participating in community art projects and documenting local history.',
              image: 'https://firebasestorage.googleapis.com/v0/b/placeholder-audio.appspot.com/o/placeholder-avatar.png?alt=media&token=placeholder',
              website: 'https://example.com/sarah-johnson',
              social: '@sarahjstories',
              tags: {
                authorTags: ['community-member', 'storyteller', 'local-history']
              },
              isClaimed: true,
              userKey: 'demo-user-submission-1'
            }
          };
          
          DemoDataManager.createRecording(dummySubmission);
              RecordingStore.addRecording(dummySubmission);
          submissions = [dummySubmission];
        } else {
          logger.info('Submission already exists, returning existing', { 
            submissionCount: finalSubmissionsCheck.length 
          });
              finalSubmissionsCheck.forEach(submission => {
            RecordingStore.addRecording(submission);
          });
          return finalSubmissionsCheck;
        }
      }
    }
    
    // Ensure all submissions are in RecordingStore before returning
    if (submissions.length > 0) {
      submissions.forEach(submission => {
        RecordingStore.addRecording(submission);
      });
    }
    
    return submissions;
  }

  try {
    const recordings = await BaseCRUDManager.query<IRecording>(
      RECORDINGS_COLLECTION,
      {
        filters: [
          { field: 'project', operator: '==', value: projectId },
          { field: 'isUserRecording', operator: '==', value: true }
        ]
      },
      { idField: 'key' }
    );
    
    const queryLogKey = `query_${projectId}`;
    if (!(window as any)[queryLogKey]) {
      logger.info('Firebase query executed', { 
        projectId, 
        totalDocs: recordings.length
      });
      (window as any)[queryLogKey] = true;
      setTimeout(() => delete (window as any)[queryLogKey], 5000);
    }
    
    const submissions: IRecordingWithAuthor[] = [];
    
    for (const recording of recordings) {
      if (recording.isSubmission === true) {
        const submissionLogKey = `submission_${recording.key}`;
        if (!(window as any)[submissionLogKey]) {
          logger.info('Recording qualifies as submission', { 
            key: recording.key,
            isSubmission: recording.isSubmission,
            approvalStatus: recording.approvalStatus,
            submittedAt: recording.submittedAt
          });
          (window as any)[submissionLogKey] = true;
        }
        const submissionWithAuthor = await attachAuthorToEntity(recording);
        submissions.push(submissionWithAuthor);
      }
    }
    
    const summaryLogKey = `summary_${projectId}`;
    if (!(window as any)[summaryLogKey]) {
      const userRecordingsNotSubmissions = recordings.filter(r => 
        r.isUserRecording && r.isSubmission !== true
      );
      
      logger.info('Firebase mode: all user recordings details', {
        projectId,
        recordings: recordings.map(r => ({
          key: r.key,
          isUserRecording: r.isUserRecording,
          isSubmission: r.isSubmission,
          approvalStatus: r.approvalStatus,
          submittedAt: r.submittedAt,
          approvedAt: (r as any).approvedAt,
          approvedBy: (r as any).approvedBy,
          pinKey: r.pinKey,
          project: r.project,
          allFields: Object.keys(r)
        }))
      });
      
      logger.info('Firebase mode: filtered submissions', { 
        projectId, 
        submissionCount: submissions.length,
        totalUserRecordings: recordings.length,
        userRecordingsNotMarkedAsSubmissions: userRecordingsNotSubmissions.length,
        submissions: submissions.map(r => ({
          key: r.key,
          isSubmission: r.isSubmission,
          approvalStatus: r.approvalStatus,
          submittedAt: r.submittedAt
        })),
        recordingsNotSubmissions: userRecordingsNotSubmissions.map(r => ({
          key: r.key,
          note: 'This recording has isUserRecording=true but isSubmission is not true',
          isSubmission: r.isSubmission
        }))
      });
      (window as any)[summaryLogKey] = true;
      setTimeout(() => delete (window as any)[summaryLogKey], 5000);
    }
    
    // Sort by createdAt descending (newest first)
    return submissions.sort(sortByDate(sub => sub.createdAt, 'desc'));
  } catch (error) {
    logger.error('Error fetching submissions by project', { projectId, error });
    return [];
  }
}

/**
 * Listen to submissions for a project in real-time
 */
export function listenToSubmissions(
  projectId: string,
  callback: (submissions: IRecordingWithAuthor[]) => void
): (() => void) {
  if (isDemoMode()) {
    const checkInterval = setInterval(async () => {
      const submissions = await getSubmissionsByProject(projectId);
      callback(submissions);
    }, 5000);
    
    getSubmissionsByProject(projectId).then(callback).catch(error => {
      logger.error('Error in initial submissions fetch', { projectId, error });
      callback([]);
    });
    
    return () => clearInterval(checkInterval);
  }

  try {
    const recordingsRef = collection(db, RECORDINGS_COLLECTION);
    const q = query(
      recordingsRef,
      where('project', '==', projectId),
      where('isUserRecording', '==', true)
    );
    
    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const submissions: IRecordingWithAuthor[] = [];
      
      const listenerLogKey = `listener_${projectId}`;
      const shouldLog = !(window as any)[listenerLogKey];
      if (shouldLog) {
        logger.debug('Listener: snapshot received', {
          projectId,
          docCount: snapshot.docs.length
        });
        (window as any)[listenerLogKey] = true;
        setTimeout(() => delete (window as any)[listenerLogKey], 5000);
      }
      
      for (const docSnapshot of snapshot.docs) {
        const recording = { key: docSnapshot.id, ...docSnapshot.data() } as IRecording;
        if (recording.isSubmission === true) {
          try {
            const submissionWithAuthor = await attachAuthorToEntity(recording);
            submissions.push(submissionWithAuthor);
            logger.info('Listener: added submission', { 
              key: recording.key,
              isSubmission: recording.isSubmission,
              approvalStatus: recording.approvalStatus,
              submittedAt: recording.submittedAt
            });
          } catch (error) {
            logger.error('Error fetching author for submission', { key: recording.key, error });
            submissions.push({ ...recording, author: null } as IRecordingWithAuthor);
          }
        }
      }
      
      if (submissions.length > 0 && shouldLog) {
        logger.info('Listener: filtered submissions', {
          projectId,
          submissionCount: submissions.length,
          submissions: submissions.map(s => ({
            key: s.key,
            approvalStatus: s.approvalStatus,
            submittedAt: s.submittedAt
          }))
        });
      }
      
      // Sort by createdAt descending
      submissions.sort((a, b) => {
        const aTime = a.createdAt?.toMillis() ?? 0;
        const bTime = b.createdAt?.toMillis() ?? 0;
        return bTime - aTime;
      });
      
      callback(submissions);
    }, (error) => {
      console.error('Error listening to submissions:', error);
    });
    
    return unsubscribe;
  } catch (error) {
    console.error('Error setting up submission listener:', error);
    return () => {};
  }
}
